import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus, Send, Users } from 'lucide-react';
import { Button, PageHeader, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { toast } from '@/store/toastStore';
import { listUtilisateurs } from '@/services/parametres';
import {
  type Conversation,
  envoyer,
  getConversation,
  listConversations,
} from '@/services/messagerie';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const fmtHeure = (s: string) => { try { return new Date(s).toLocaleString('fr-DZ', { dateStyle: 'short', timeStyle: 'short' }); } catch { return s; } };

export function MessageriePage() {
  const qc = useQueryClient();
  const [selId, setSelId] = useState<string | null>(null);   // interlocuteur sélectionné
  const [selNom, setSelNom] = useState<string | null>(null);
  const [nouveau, setNouveau] = useState(false);
  const [texte, setTexte] = useState('');
  const filEnd = useRef<HTMLDivElement>(null);

  const { data: convs, isLoading: lc } = useQuery({
    queryKey: ['msg-conversations'], queryFn: listConversations, refetchInterval: 20000,
  });
  const { data: fil } = useQuery({
    queryKey: ['msg-fil', selId], queryFn: () => getConversation(selId!), enabled: !!selId,
  });
  const { data: users } = useQuery({ queryKey: ['utilisateurs'], queryFn: listUtilisateurs });

  // Ouvrir une conversation marque ses messages lus côté serveur → rafraîchit liste + badge.
  useEffect(() => {
    if (fil) {
      qc.invalidateQueries({ queryKey: ['msg-conversations'] });
      qc.invalidateQueries({ queryKey: ['messagerie-nonlus'] });
    }
  }, [fil, qc]);
  useEffect(() => { filEnd.current?.scrollIntoView({ block: 'end' }); }, [fil]);

  const ouvrir = (id: string, nom: string | null) => { setSelId(id); setSelNom(nom); setNouveau(false); };

  const sendMut = useMutation({
    mutationFn: () => {
      const t = texte.trim();
      return envoyer({ destinataire_id: selId!, objet: t.slice(0, 80) || 'Message', corps: t, priorite: 'NORMALE' });
    },
    onSuccess: () => {
      setTexte('');
      qc.invalidateQueries({ queryKey: ['msg-fil', selId] });
      qc.invalidateQueries({ queryKey: ['msg-conversations'] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const messages = fil?.messages ?? [];

  return (
    <>
      <PageHeader title="Messagerie interne" subtitle="Conversations entre membres de l'équipe"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Messagerie' }]} />

      <div className="grid h-[70vh] grid-cols-1 gap-3 sm:grid-cols-[300px_1fr]">
        {/* Volet gauche : conversations */}
        <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface-secondary">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-sm font-semibold text-content-primary">Conversations</span>
            <Button size="sm" variant="secondary" onClick={() => setNouveau((v) => !v)}><MessageSquarePlus className="size-4" /> Nouveau</Button>
          </div>
          {nouveau && (
            <div className="border-b border-border bg-surface-primary p-2">
              <p className="mb-1 flex items-center gap-1 text-xs text-content-muted"><Users className="size-3.5" /> Démarrer avec…</p>
              <select className={selectCls} defaultValue="" onChange={(e) => { const u = users?.find((x) => x.id === e.target.value); if (u) ouvrir(u.id, `${u.prenom} ${u.nom}`); }}>
                <option value="">— Choisir un destinataire —</option>
                {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
              </select>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {lc ? <Skeleton className="m-3 h-40" /> : (convs ?? []).length === 0 ? (
              <p className="p-6 text-center text-sm text-content-muted">Aucune conversation. Cliquez « Nouveau » pour en démarrer une.</p>
            ) : (convs ?? []).map((c: Conversation) => (
              <button key={c.interlocuteur_id} onClick={() => ouvrir(c.interlocuteur_id, c.interlocuteur_nom)}
                className={cn('flex w-full flex-col gap-0.5 border-b border-border px-3 py-2.5 text-left hover:bg-surface-hover', selId === c.interlocuteur_id && 'bg-surface-hover')}>
                <div className="flex items-center justify-between gap-2">
                  <span className={cn('truncate text-sm text-content-primary', c.non_lus > 0 && 'font-bold')}>{c.interlocuteur_nom ?? 'Utilisateur'}</span>
                  {c.non_lus > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-status-stuck px-1.5 text-[10px] font-bold text-white">{c.non_lus}</span>}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className={cn('truncate text-xs', c.non_lus > 0 ? 'text-content-secondary' : 'text-content-muted')}>{c.dernier_de_moi ? 'Vous : ' : ''}{c.dernier_corps || c.dernier_objet}</span>
                  <span className="shrink-0 text-[10px] tabular-nums text-content-muted">{fmtHeure(c.date_dernier)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Volet droit : fil */}
        <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface-secondary">
          {!selId ? (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-content-muted">Sélectionnez une conversation, ou démarrez-en une nouvelle.</div>
          ) : (
            <>
              <div className="border-b border-border px-4 py-2.5"><span className="font-semibold text-content-primary">{selNom ?? fil?.interlocuteur_nom ?? 'Conversation'}</span></div>
              <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
                {messages.length === 0 ? (
                  <p className="py-8 text-center text-sm text-content-muted">Aucun message. Écrivez le premier ci-dessous.</p>
                ) : messages.map((m) => {
                  const deMoi = m.expediteur_id !== selId;  // seuls 2 interlocuteurs : si ≠ eux → c'est moi
                  return (
                    <div key={m.id} className={cn('flex', deMoi ? 'justify-end' : 'justify-start')}>
                      <div className={cn('max-w-[78%] rounded-lg px-3 py-2', deMoi ? 'bg-brand-primary text-white' : 'bg-surface-primary text-content-primary')}>
                        <p className="whitespace-pre-wrap break-words text-sm">{m.corps || m.objet}</p>
                        <p className={cn('mt-0.5 text-[10px] tabular-nums', deMoi ? 'text-white/70' : 'text-content-muted')}>{fmtHeure(m.date_envoi)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={filEnd} />
              </div>
              <form className="flex items-end gap-2 border-t border-border p-2" onSubmit={(e) => { e.preventDefault(); if (texte.trim()) sendMut.mutate(); }}>
                <textarea className={cn(selectCls, 'h-10 max-h-28 min-h-10 flex-1 resize-none py-2')} placeholder="Votre message…" value={texte}
                  onChange={(e) => setTexte(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (texte.trim()) sendMut.mutate(); } }} />
                <Button type="submit" loading={sendMut.isPending} disabled={!texte.trim()}><Send className="size-4" /></Button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}
